import {
  type AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { filter, ReplaySubject, Subscription, take, takeWhile } from 'rxjs';
import type { MessageSeen } from '../../../server/models/message-seen.model';
import type {
  Message,
  TitleUpdate,
  UserUpdate,
} from '../../../server/types/message-types';
import type { OrgUser } from '../../../server/types/org-types';
import { AuthUserService, unknownUser } from '../auth-user.service';
import { ImageService } from '../image.service';
import {
  type DisplayMessage,
  type MessageSubmitted,
  ThreadItem,
} from '../message.service';
import { SettingsService, type ThemeString } from '../settings.service';

dayjs.extend(calendar);

type UserUpdateMessageDisplay = {
  updateType: 'user';
  update: UserUpdate & { index: number };
};

type TitleUpdateMessageDisplay = {
  updateType: 'title';
  update: TitleUpdate & { index: number };
};

@Component({
  selector: 'app-thread-message',
  templateUrl: './thread-message.component.html',
  styleUrls: ['./thread-message.component.scss'],
  standalone: false,
})
export class ThreadMessageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private authUserService = inject(AuthUserService);
  private dialogService = inject(MatDialog);
  private settingsService = inject(SettingsService);
  private imageService = inject(ImageService);

  @Input() threadItem: ThreadItem;
  @Input() displayMessage: DisplayMessage;
  @Input() isGroup: boolean;
  @Input()
  messageSeensReplaySubject: ReplaySubject<MessageSeen[]>;

  @ViewChild('outerMessage') outerMessage: ElementRef;

  message?: Message | MessageSubmitted;

  user: OrgUser;
  displaySenderUser = false;
  displayTopTime = true;
  displaySubTime = true;
  stacked = false;
  backgroundColor: number;

  theme: ThemeString;

  authUserId: string;
  orgId: string;
  refId: string;
  number: number;
  userId: string;
  updates: (UserUpdateMessageDisplay | TitleUpdateMessageDisplay)[] = [];
  body: string;
  createdAt: number;

  error = false;
  errorString?: string;

  mediaUrl?: string;
  imageDialogUrl?: string;

  isoTime: string;
  topTime: string;
  subTime: string;

  imageDialogReference;

  seen = false;
  intersectionObserver: IntersectionObserver | undefined;

  messageSeens: MessageSeen[] = [];

  messageStatus = 'done';

  showMenuButton = false;

  ready = false;
  subscriptions = new Subscription();

  ngOnInit(): void {
    if (!this.messageSeensReplaySubject) {
      throw new Error(
        'messageSeensReplaySubject is not set in message component from thread-view component.'
      );
    }

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.theme = theme)
    );
    this.subscriptions.add(themeSubscription);

    this.updateInitialMessageFields();
    if (this.displayMessage.message) {
      this.updateFirmMessageFields(this.displayMessage.message);
    }
    this.displayMessage.messageObservable?.subscribe(
      (message) => {
        this.updateFirmMessageFields(message);
      },
      (err) => {
        this.error = true;
        console.error('Error in the message component');
        console.error(err);
      }
    );

    const senderUser = this.authUserService.getUser({
      orgId: this.orgId,
      userId: this.userId,
    });

    this.user = senderUser ?? unknownUser({ userId: this.userId });

    this.backgroundColor = this.user.color;

    this.ready = true;
    this.latestSeenSubscriptionSetup();

    this.displayMessage.previousMessage.subscribe((previous) => {
      this.displayTopTime = true;
      this.displaySubTime = true;
      this.stacked = false;
      this.displaySenderUser = this.isGroup && this.userId !== this.authUserId; // Only show if group and for other users

      if (previous) {
        // Determine whether to hide the sub time
        if (this.createdAt < previous.createdAt + 15 * 60 * 1000) {
          this.displaySubTime = false;
        }

        // Determine whether to show the top date
        const thisDate = new Date(this.createdAt).toDateString();
        const previousDate = new Date(previous.createdAt).toDateString();
        if (thisDate === previousDate) {
          this.displayTopTime = false;
        }

        // Determine whether to hide the sender name
        // Determine whether to round the top
        // If the previous message is from the same user and not a lot of time has passed
        if (previous.userId === this.userId && !this.displayTopTime) {
          this.displaySenderUser = false;
          this.stacked = true;
        }
      }
    });
  }

  ngAfterViewInit() {
    if (this.displayMessage.message)
      this.startOwnSeenTrigger(this.displayMessage.message);
  }

  updateInitialMessageFields(): void {
    const message = this.displayMessage.message;

    if (!message) throw new Error('unexpected state for displayMessage');

    const { orgId, refId, number, userId, body, createdAt } = message;
    const authUserId = this.authUserService.getAuthUserId({ orgId });
    this.authUserId = authUserId;
    this.orgId = orgId;
    this.refId = refId;
    this.number = number;
    this.userId = userId;
    this.body = body;
    this.createdAt = createdAt;
    this.isoTime = new Date(this.createdAt).toISOString();
    this.topTime = dayjs(this.createdAt).format('ll');
    this.subTime = dayjs(this.createdAt).format('LT');

    // Display title and user updates below this message
    if (this.threadItem.thread) {
      const currentTitleUpdates = this.threadItem.thread?.titleUpdates ?? [];
      currentTitleUpdates
        .filter((u) => u.messageNumber === this.number)
        .map((u, index) => ({ ...u, index }))
        .map((u) => this.handleTitleUpdate(u));
    }

    const currentUserUpdates =
      this.threadItem.thread?.userUpdates ?? this.threadItem.task?.userUpdates;
    currentUserUpdates
      ?.filter((u) => u.messageNumber === this.number)
      .map((u, index) => ({ ...u, index }))
      .map((u) => this.handleUserUpdate(u));

    const titleSub = this.threadItem.titleUpdateObservable
      .pipe(filter((u) => u.messageNumber === number))
      .subscribe((u) => this.handleTitleUpdate(u));
    this.subscriptions.add(titleSub);

    const updateUsersSub = this.threadItem.userUpdateObservable
      .pipe(filter((u) => u.messageNumber === number))
      .subscribe((u) => this.handleUserUpdate(u));
    this.subscriptions.add(updateUsersSub);
  }

  handleTitleUpdate(update: TitleUpdate & { index: number }) {
    if (update.index === 0) return; // Don't display initial title update
    let i = this.updates.findIndex(
      (u) => update.updatedAt < u.update.updatedAt
    );
    if (i === -1) i = this.updates.length;
    this.updates.splice(i, 0, { updateType: 'title', update });
  }

  handleUserUpdate(update: UserUpdate & { index: number }) {
    if (update.index === 0 && this.threadItem.mode === 'thread') return; // Don't display initial user update in thread mode
    let i = this.updates.findIndex(
      (u) => update.updatedAt < u.update.updatedAt
    );
    if (i === -1) i = this.updates.length;
    this.updates.splice(i, 0, { updateType: 'user', update });
  }

  updateFirmMessageFields(message: Message): void {
    this.message = message;
    this.createdAt = message.createdAt;
  }

  launchImageDialog(imageDialog: TemplateRef<any>): void {
    if (this.message?.files && this.message?.files[1]) {
      this.imageDialogUrl = this.imageService.getImageUrl({
        fileId: this.message?.files[1]._id,
      });
      this.imageDialogReference = this.dialogService.open(imageDialog, {
        width: '500px',
      });
    }
  }

  startOwnSeenTrigger(message: Message): void {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.intersectionObserver?.disconnect();
          if (this.seen) return; // If it has already been seen, bail
          this.seen = true;

          const { number } = message;
          this.threadItem.setSeen({ number });
        }
      });
    });
    const ele = this.outerMessage.nativeElement as HTMLElement;
    this.intersectionObserver.observe(ele);

    const ownSeenStartSubscription =
      this.threadItem.messageNumberSeenReplayObservable
        .pipe(take(1))
        .subscribe((latestNumberSeen) => {
          // If the latest seen is after this message, don't start the subscription
          if (latestNumberSeen >= this.number) {
            this.seen = true;
            return;
          }
        });
    this.subscriptions.add(ownSeenStartSubscription);

    const ownSeenCancelSubscription =
      this.threadItem.messageNumberSeenReplayObservable
        .pipe(takeWhile(() => !this.seen))
        .subscribe((number) => {
          // If the latest seen is after this message, cancel the interval
          if (number >= this.number) {
            this.seen = true;
            if (this.intersectionObserver)
              this.intersectionObserver.disconnect();
            return;
          }
        });
    this.subscriptions.add(ownSeenCancelSubscription);
  }

  latestSeenSubscriptionSetup(): void {
    const latestSeenSubscribe = this.messageSeensReplaySubject.subscribe(
      (newMessageSeens) => {
        newMessageSeens.forEach((newMessageSeen) => {
          if (newMessageSeen.number === this.number) {
            // If the seen status is not in the this.messageSeens, add
            // this seenMessage. If it is already in this message, do nothing.
            if (
              !this.messageSeens.find((x) => x.userId === newMessageSeen.userId)
            ) {
              this.messageSeens.push(newMessageSeen);
            }
          } else {
            // Remove the messageSeens if they have moved to another
            // message (this.messageId !== newMessageSeen.messageId)

            const index = this.messageSeens.findIndex(
              (x) => x.userId === newMessageSeen.userId
            );
            if (index !== -1) this.messageSeens.splice(index, 1);
          }
        });
      }
    );
    this.subscriptions.add(latestSeenSubscribe);
  }

  toggleSubTime(): void {
    this.displaySubTime = !this.displaySubTime;
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) this.intersectionObserver.disconnect();

    this.subscriptions.unsubscribe();
  }
}
